/**
 * Inside a chase binder.
 *
 * A chase binder holds cards the person does not own yet, so the cards come from
 * `wish_binder_cards` rather than from `cards.binder_id`, which points at owned
 * rows only.
 *
 * IT LOOKS LIKE A MASTER SET, BECAUSE IT IS THE SAME KIND OF THING.
 *
 * Chase's review of phase 5: "when you go inside the binder, it's not set up the
 * same way that these master set or subsets are... they should have the filter,
 * they should have the cost, they should have all the things that we have
 * everywhere else, but we need to make sure that it's fading the ones they don't
 * have and highlighting the ones they do have." So the gauge, the toolbar and the
 * tile are the slot grid's own, imported from slotGridParts rather than
 * re-described here.
 *
 * What is NOT shared is the data path, and that is the whole design. A master
 * set's slots come from the catalogue crossed with its printings and are resolved
 * by `wish_set_slot_grid`; a chase binder's slots are the rows somebody put in
 * it, so its denominator is the binder's own size and its ownership is the
 * plainer question of whether they hold that catalogue card - see binderSlots.js
 * and services/wishOwned.js.
 *
 * THE PRICE ON A TILE IS LOOKED UP, NEVER STORED.
 *
 * `wish_binder_cards` carries no price column and is not going to get one. A
 * price written into a row is right for a day and wrong after that, and this app
 * already runs a pricing refresh that owns that number. The catalogue read that
 * puts these tiles in order returns `near_mint` on every row it hands back, so
 * the money arrives with the order for free - the same column, from the same
 * view, that the picker's tiles and the master set's tiles quote.
 *
 * A TAP OPENS THE CARD, unlike the master set grid, where three tiles in four
 * are printings nobody owns and there is no card row behind them. Every tile in
 * here is a real catalogue card somebody deliberately added, so the card detail
 * screen has something to show.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRegisterGridControls } from '../../context/GridControlsContext';
import useZoom from '../../hooks/useZoom';
import { Plus } from 'lucide-react';
import { zoomGrid } from '../collection/CatalogCardGrid';
import SortFilterModal from '../modals/SortFilterModal';
import { fetchWishBinderCards } from '../../services/wishBinders';
import { fetchOwnedCardIds } from '../../services/wishOwned';
import * as cardData from '../../services/cardData';
import { isTcgPocketCardId } from '../../utils/tcgPocket';
import {
  hasCatalogFilters, countCatalogFilters, EMPTY_CATALOG_FILTERS,
} from '../../services/cardData/catalogFilters';
import {
  readBinderGridPrefs, writeBinderGridPrefs, DEFAULT_SET_GRID_SORT,
} from './setSlotPrefs';
import { buildBinderTiles, binderProgress } from './binderSlots';
import {
  SlotGridHeader, SlotTile, slotGridStyles,
} from './slotGridParts';
import haptics from '../../utils/haptics';

/**
 * The most cards one catalogue read asks for in a single request.
 *
 * A binder is unlimited by design, and the order has to be the database's, which
 * means every id travels in one URL: 800 ids is about 6 KB, which PostgREST
 * answers without complaint. Past this the tail is still drawn - from what the
 * binder rows themselves stored - it simply is not re-ordered, and the log says
 * so rather than the screen quietly lying about the sort.
 */
const ID_CAP = 800;

/** The same 60-tile paging the master set grid uses, for the same reason. */
const FIRST_PAGE = 60;
const PAGE_STEP = 60;

export default function WishBinderDetail({
  binder,
  theme,
  isDark = true,
  asUserId = null,
  pricingEnabled = false,
  readOnly = false,
  onBack,
  onOpenCard,
  onAddCards,
}) {
  const [rows, setRows] = useState(null);
  const [cards, setCards] = useState(null);
  const [owned, setOwned] = useState(null);
  const [failed, setFailed] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [shown, setShown] = useState(FIRST_PAGE);

  // Read once on the way in rather than on every render of a grid that can hold
  // hundreds of tiles. Its own storage key, so a choice made in here cannot
  // re-order Explore or a master set.
  // Zoom is the app's one preference, not this screen's. See
  // utils/zoomPreference.js: sort and filter are remembered per place because
  // they are about the task, zoom is not because it is about your eyes.
  const { zoom, cycleZoom } = useZoom();
  const [sort, setSort] = useState(() => readBinderGridPrefs().sort);
  const [filters, setFilters] = useState(() => readBinderGridPrefs().filters);
  const [missingOnly, setMissingOnly] = useState(() => readBinderGridPrefs().missingOnly);

  useEffect(() => {
    writeBinderGridPrefs({ sort, filters, missingOnly });
  }, [sort, filters, missingOnly]);

  const [eras, setEras] = useState([]);
  useEffect(() => {
    let alive = true;
    cardData.getEras()
      .then((list) => { if (alive) setEras(list || []); })
      .catch((e) => console.warn('[chase binder] era load failed', e?.message));
    return () => { alive = false; };
  }, []);

  // The binder's contents: the denominator, and the only list of ids there is.
  useEffect(() => {
    let alive = true;
    setRows(null);
    fetchWishBinderCards(binder.id)
      .then((found) => { if (alive) setRows(found); })
      .catch((e) => {
        console.warn('[chase binder] could not read cards', e?.message);
        if (alive) { setRows([]); setFailed(true); }
      });
    return () => { alive = false; };
  }, [binder.id]);

  const ids = useMemo(
    () => (rows || []).map((r) => r.tcgdex_card_id).filter(Boolean),
    [rows],
  );

  // The order and the filtering are the database's, the same as Explore and the
  // master set grid, so the binder never sorts rows it can only see a page of.
  useEffect(() => {
    if (rows === null) return undefined;
    let alive = true;
    setCards(null);
    if (ids.length === 0) { setCards([]); return undefined; }
    if (ids.length > ID_CAP) {
      console.warn('[chase binder] %s holds %d cards; only the first %d are sorted by the catalogue',
        binder.name, ids.length, ID_CAP);
    }
    cardData.listCatalogCards({ ids: ids.slice(0, ID_CAP), from: 0, to: ID_CAP, sort, filters })
      .then(({ rows: found }) => { if (alive) setCards(found || []); })
      .catch((e) => {
        console.warn('[chase binder] could not read the catalogue', e?.message);
        // The tiles fall back to what the binder rows stored, which is why a
        // failed catalogue read costs the order and the price rather than the
        // whole screen.
        if (alive) setCards([]);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, sort, filters]);

  // Ownership for the ACTING profile, never for whoever is signed in. A parent
  // switched into a child is looking at the child's binder, so the fading and
  // the gauge have to be the child's - phase 5 fixed the same thing on the
  // master set bar. On somebody else's list nothing is asked for at all: their
  // progress is not ours to draw, and ours is not theirs.
  useEffect(() => {
    if (readOnly || rows === null) { setOwned(null); return undefined; }
    let alive = true;
    if (ids.length === 0) { setOwned(new Set()); return undefined; }
    fetchOwnedCardIds(ids, asUserId)
      .then((found) => { if (alive) setOwned(found); })
      .catch((e) => {
        console.warn('[chase binder] ownership lookup failed', e?.message);
        if (alive) setOwned(new Set());
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, asUserId, readOnly]);

  const tiles = useMemo(() => {
    if (rows === null || cards === null) return [];
    return buildBinderTiles({
      rows,
      cards,
      owned,
      ownership: !readOnly,
      missingOnly: readOnly ? false : missingOnly,
      filtered: hasCatalogFilters(filters),
    });
  }, [rows, cards, owned, readOnly, missingOnly, filters]);

  // Counted over the binder's rows, not over the tiles on screen: filtering to
  // Grass narrows the grid and must never rewrite what the gauge claims.
  const progress = useMemo(
    () => binderProgress(rows || [], owned, !readOnly),
    [rows, owned, readOnly],
  );

  useEffect(() => { setShown(FIRST_PAGE); }, [sort, filters, missingOnly, binder.id]);

  const sentinel = useRef(null);
  const grow = useCallback(() => setShown((n) => n + PAGE_STEP), []);
  useEffect(() => {
    const node = sentinel.current;
    if (!node || shown >= tiles.length) return undefined;
    if (typeof IntersectionObserver === 'undefined') { grow(); return undefined; }
    // The app scrolls inside .app-content rather than the document, the same
    // scroller CardImage has to be told about.
    const root = node.closest('.app-content') || null;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) grow();
    }, { root, rootMargin: '400px 0px', threshold: 0.01 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [shown, tiles.length, grow]);

  const loading = rows === null || cards === null;
  const empty = !loading && (rows || []).length === 0;

  /* On the page's toolbar beside the search, not on a row of their own. An
     empty binder offers nothing to size or sort, so it registers nothing. */
  useRegisterGridControls(empty ? {} : {
    onZoom: cycleZoom,
    onSort: () => setSortOpen(true),
    sortActive: hasCatalogFilters(filters),
    filterCount: countCatalogFilters(filters),
    missingOnly: readOnly ? null : missingOnly,
    onMissingOnly: readOnly ? null : () => setMissingOnly((v) => !v),
  }, [empty, cycleZoom, filters, missingOnly, readOnly]);

  return (
    <div style={styles.wrap}>
      {/* Name on one line, the gauge Chase asked for on the next, and the
          controls under both - the rhythm the Binder tab's own header keeps,
          where `130 cards  $5,420.42` is one line rather than a panel.

          The fraction is the only place the size is said now. It used to be
          said twice, as "24 cards wanted" on the title block and again as
          "0 of 24 cards" in the panel below it, which cost a line to repeat a
          number. Its denominator is still the binder's own size: "if they only
          have ten cards in there or fifty cards, it should be filling up to the
          amount of cards in that particular custom binder." */}
      <SlotGridHeader
        title={binder.name}
        onBack={onBack}
        backLabel="Back to the wish list"
        theme={theme}
        progress={empty
          ? null
          : {
            total: progress.total,
            filled: progress.filled,
            noun: 'cards',
            pending: rows === null,
          }}
        /* An empty binder gets its name and nothing else on this line. The
           paragraph under the grid already says it is empty AND says what the
           (+) is for, and saying it twice in two places is worse than once in
           the place that can explain it. */
        meta={null}
        testId={empty ? undefined : 'binder-progress'}
        hint={!empty && readOnly
          ? "Whose cards are whose is not shown on somebody else's list."
          : null}
        toolbar={null}
      />

      {loading && (
        <p style={{ ...styles.state, color: theme.textSecondary }}>Laying out the binder...</p>
      )}
      {!loading && failed && (
        <p style={{ ...styles.state, color: theme.textSecondary }}>
          This binder could not be read just now.
        </p>
      )}

      <div style={{ ...styles.grid, ...zoomGrid(zoom) }}>
        {/* The (+) stays first, because it is the only door into the picker and
            an empty binder has nothing else to offer. */}
        {!readOnly && (
          <button
            type="button"
            style={{ ...styles.addSlot, backgroundColor: theme.bgCard, borderColor: theme.border }}
            onClick={() => onAddCards?.()}
            aria-label="Add cards to this binder"
          >
            <Plus size={20} color={theme.textMuted} />
          </button>
        )}
        {tiles.slice(0, shown).map((tile) => (
          <SlotTile
            key={tile.id}
            tile={tile}
            captions={zoom > 0}
            theme={theme}
            isDark={isDark}
            pocket={isTcgPocketCardId(tile.cardId)}
            pricingEnabled={pricingEnabled}
            neutral={readOnly}
            onOpen={() => { haptics.tap(); onOpenCard?.(tile.row); }}
          />
        ))}
      </div>

      <div ref={sentinel} style={styles.sentinel}>
        {shown < tiles.length && (
          <span style={{ ...styles.state, color: theme.textSecondary }}>
            {tiles.length - shown} more cards...
          </span>
        )}
      </div>

      {empty && (
        <p style={{ ...styles.empty, color: theme.textMuted }}>
          Nothing in here yet. The (+) is how it fills up.
        </p>
      )}
      {!loading && !empty && tiles.length === 0 && (
        <p style={{ ...styles.state, color: theme.textSecondary }}>
          {missingOnly
            ? 'Nothing missing here. You have every card in this binder.'
            : 'No cards in here match all of that. Try unticking one.'}
        </p>
      )}

      <SortFilterModal
        isOpen={sortOpen}
        onClose={() => setSortOpen(false)}
        /* Catalogue mode, which is the set of orders a fixed list of cards can
           honestly be put in - and it carries no Best Match, which would mean
           nothing over a list nobody searched for. */
        mode="catalog"
        isOwnCollection
        sortBy={sort}
        setSortBy={setSort}
        catalogFilters={filters}
        setCatalogFilters={setFilters}
        catalogEras={eras}
        onReset={() => { setFilters(EMPTY_CATALOG_FILTERS); setSort(DEFAULT_SET_GRID_SORT); }}
      />
    </div>
  );
}

const styles = {
  ...slotGridStyles,
  wrap: { display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px 12px 24px' },
  addSlot: {
    // An empty square reads as an invitation rather than as a card that failed
    // to load, which is what the dashed edge is doing here.
    position: 'relative', width: '100%', aspectRatio: '5 / 7', borderRadius: '8px',
    border: '1px dashed', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', padding: 0, outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  empty: { fontSize: '13px', margin: 0, lineHeight: 1.45 },
};
